import { Coordinates } from 'data/model'
import { i18n } from 'lib/i18n'

import { UnitType } from './storage'

const METER_TO_FEET = 3.28084
const METER_TO_YARD = 1.09361
const FEET_TO_MILE = 5280
const YARD_TO_MILE = 1760
const METER_TO_KILOMETER = 1000
const THRESHOLD = 975

interface DistanceAndUnit {
  newDistance: number
  unit: string
}
export interface CountryAndUnit {
  countryCode?: string | null
  unit: string
}

function toImperialUS(distance: number): DistanceAndUnit {
  const distanceInFoot = distance * METER_TO_FEET

  return distanceInFoot < THRESHOLD
    ? { newDistance: distanceInFoot, unit: 'foot' }
    : { newDistance: distanceInFoot / FEET_TO_MILE, unit: 'mile' }
}
function toImperialUK(distance: number): DistanceAndUnit {
  const distanceInYard = distance * METER_TO_YARD

  return distanceInYard < THRESHOLD
    ? { newDistance: distanceInYard, unit: 'yard' }
    : { newDistance: distanceInYard / YARD_TO_MILE, unit: 'mile' }
}
function toMetric(distance: number): DistanceAndUnit {
  return distance > THRESHOLD
    ? { newDistance: distance / METER_TO_KILOMETER, unit: 'kilometer' }
    : { newDistance: distance, unit: 'meter' }
}
function getAutomaticDistance(distance: number, countryCode?: string | null): DistanceAndUnit {
  if (countryCode === 'international') {
    return toMetric(distance)
  }

  return countryCode === 'US'
    ? toImperialUS(distance)
    : countryCode === 'GB'
    ? toImperialUK(distance)
    : toMetric(distance)
}

export function formatDistance(
  distance: number,
  { countryCode, unit: unitInStorage }: CountryAndUnit
): string {
  const { newDistance, unit } =
    unitInStorage === UnitType.imperialUS
      ? toImperialUS(distance)
      : unitInStorage === UnitType.imperialUK
      ? toImperialUK(distance)
      : unitInStorage === UnitType.metric
      ? toMetric(distance)
      : getAutomaticDistance(distance, countryCode)

  return newDistance.toLocaleString(navigator.language, {
    maximumFractionDigits: 1,
    style: 'unit',
    unit: unit,
    unitDisplay: 'short',
  })
}

export function formatLocalTimeDuration(seconds: number): string {
  const hoursLabel = i18n('time-unit.hour')
  const minutesLabel = i18n('time-unit.minutes')
  const daysLabel = i18n('duration.day.abbrev')
  let minutes = 0
  let hours = 0
  let days = 0

  if (seconds < 60) {
    return `< 1 ${minutesLabel}`
  }
  if (seconds >= 60) {
    minutes = Math.floor(seconds / 60)
  }
  if (minutes >= 60) {
    hours = Math.floor(minutes / 60)
    minutes = minutes % 60
  }
  if (hours >= 24) {
    days = Math.floor(hours / 24)
    hours = hours % 24
  }

  return `${days > 0 ? `${days} ${daysLabel} ` : ''}${hours > 0 ? `${hours} ${hoursLabel} ` : ''}${
    minutes > 0 ? `${minutes} ${minutesLabel}` : ''
  }`
}

export function formatExternalLinkText(value: string): string {
  return value.replace(/^(http(s)?:\/\/)?(www\.)?/, '')
}

export function calculateDistance(start: Coordinates, end: Coordinates): number {
  const startPoint = new H.geo.Point(start.lat, start.lng)
  const endPoint = new H.geo.Point(end.lat, end.lng)
  return Math.ceil(startPoint.distance(endPoint))
}

export function calculateAndFormatDistance(
  start: Coordinates,
  end: Coordinates,
  countryAndUnit: CountryAndUnit
): string {
  return formatDistance(calculateDistance(start, end), countryAndUnit)
}

export function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export function roundDecimalNumber(num: number, scale = 0): number {
  if (scale === 0) return num
  const d = 10 ** scale
  return Math.round(num * d) / d
}
